import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { query } from "../api/client";
import type { OutcomeRow } from "../api/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, PageHeader, when } from "../components/common";

/**
 * The trial scoreboard: one row per delivered lead and whatever came back.
 *
 * `awaiting_feedback` is the default filter because silence is the most common
 * trial outcome, and a scoreboard that hides it flatters the numbers.
 */

const OUTCOME_COLOR: Record<string, "success" | "error" | "warning" | "default" | "info"> = {
  won: "success",
  lost: "error",
  bad_lead: "error",
  quoted: "info",
  contacted: "info",
  delivered: "default",
};

export function OutcomesPage() {
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  const outcomes = useResource<{ items: OutcomeRow[] }>(
    `/api/outcomes${query({ awaiting: awaitingOnly ? "true" : "", limit: 200 })}`,
  );

  const rows = outcomes.data?.items ?? [];
  const silent = rows.filter((r) => r.awaiting_feedback).length;
  const wonValue = rows.reduce((sum, r) => sum + (r.outcome === "won" ? (r.value_usd ?? 0) : 0), 0);

  return (
    <Box>
      <PageHeader
        title="Outcomes"
        subtitle="What clients reported back. This is what turns a trial into a number."
        actions={
          <Button startIcon={<RefreshIcon />} onClick={outcomes.reload}>
            Refresh
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch checked={awaitingOnly} onChange={(e) => setAwaitingOnly(e.target.checked)} />
            }
            label="Only leads with no feedback"
          />
          <Typography variant="body2" color="text.secondary">
            {rows.length} delivered · {silent} without feedback
            {wonValue > 0 ? ` · $${wonValue.toLocaleString("en-US")} won` : ""}
          </Typography>
        </Stack>
      </Paper>

      <ErrorBanner error={outcomes.error} onRetry={outcomes.reload} />
      {outcomes.loading && !outcomes.data ? <LoadingState /> : null}

      {outcomes.data && rows.length === 0 ? (
        <EmptyState
          title="Nothing delivered yet"
          hint="Outcomes appear once a lead has been routed and sent to a client."
        />
      ) : null}

      {rows.length > 0 ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Outcome</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Market</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Payee</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell>Reported</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.lead_id}-${row.client_id}`} hover>
                  <TableCell>
                    <Chip
                      label={row.outcome}
                      color={OUTCOME_COLOR[row.outcome] ?? "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.client}</TableCell>
                  <TableCell>{row.market ?? "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Link component={RouterLink} to={`/leads/${row.lead_id}`} underline="hover">{row.address ?? row.signal_key}</Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="body2" noWrap title={row.payee ?? ""}>
                      {row.payee ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.res_units ?? "—"}</TableCell>
                  <TableCell align="right">
                    {row.value_usd != null ? `$${row.value_usd.toLocaleString("en-US")}` : "—"}
                  </TableCell>
                  <TableCell>
                    {row.awaiting_feedback ? (
                      <Chip label="no feedback" variant="outlined" color="warning" />
                    ) : (
                      when(row.reported_at)
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="caption" sx={{ whiteSpace: "normal" }}>
                      {row.note ?? ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Box>
  );
}
